import React from 'react';

import { SELECT_PLAN_SECTION_ID } from '@/constants';
import { PAYWALL_NOTES } from '@/constants/campaign-notes';
import { useAccount } from '@/hooks/useAccount.ts';
import { useAppModals } from '@/hooks/useAppModals';
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver';
import {
  PAYWALL_TERMS_CHECKBOX_ID,
  usePaywallTermsCheckbox,
} from '@/hooks/usePaywallTermsCheckbox';
import { useStripeV2Plans } from '@/hooks/useStripeV2Plans';
import { AxonModule } from '@/modules/axon';
import { webSubscriptionsModule } from '@/modules/subscriptions';
import { webCampaign } from '@/modules/webCampaign';
import { PaymentDescription } from '@/pages/Paywall/components/PaymentDescription/Description';
import { PromoCode } from '@/pages/Paywall/components/PromoCode';
import { SubscriptionButton } from '@/pages/Paywall/components/SubscriptionButton';
import {
  createAxonPayloadForPlan,
  createAxonPayloadForPlans,
} from '@/utils/axonUtils';
import { scrollToById } from '@/utils/scrollToById';

import {
  SelectPlanListStyles,
  SelectPlanStyles,
  SubscriptionButtonStyles,
} from './SelectPlan.styles';

export const PaywallSectionSelectPlan: React.FC<{
  title: NonNullable<React.ReactNode> | null;
  buttonProps?: {
    onClick?: () => void;
    text?: string;
  };
  isPromoCodeVisible?: boolean;
  sectionId?: string;
}> = ({
  title,
  buttonProps,
  isPromoCodeVisible = false,
  sectionId = SELECT_PLAN_SECTION_ID,
}) => {
  const { user } = useAccount();
  const userId = user?.id || null;

  const {
    isLoadingPlans,
    monthlyPlan,
    selectedPlan,
    stripePlans,
    checkIsPlanActive,
    billingPortalLink,
  } = useStripeV2Plans();

  const { show } = useAppModals('paywall');

  const requireTermsCheckbox = webCampaign.setup.paywallNotes.includes(
    PAYWALL_NOTES.REQUIRE_TERMS_CHECKBOX,
  );
  const { isChecked: isTermsChecked, setHighlighted: setTermsHighlighted } =
    usePaywallTermsCheckbox();

  const shouldRenderDisabledWeekly = webCampaign.setup.hasDisabledWeeklyPlan;

  const handleAddToCart = React.useCallback(() => {
    if (!selectedPlan || AxonModule.hasSentAddToCart()) return;

    AxonModule.setAddToCartSent();

    const payload = createAxonPayloadForPlan(selectedPlan);

    AxonModule.pushEvent('add_to_cart', 'axon_add_to_cart', payload, userId);
  }, [selectedPlan, userId]);

  const handleViewItem = React.useCallback(() => {
    if (
      isLoadingPlans ||
      AxonModule.hasSentViewItem() ||
      stripePlans.length === 0
    ) {
      return;
    }

    AxonModule.setViewItemSent();
    const payload = createAxonPayloadForPlans(stripePlans);
    AxonModule.pushEvent('view_item', 'axon_view_item', payload, userId);
  }, [isLoadingPlans, stripePlans, userId]);

  const { ref: plansListRef } = useIntersectionObserver<HTMLDivElement>(
    handleViewItem,
    {
      threshold: 0.5,
      rootMargin: '0px',
      once: true,
    },
  );

  const handleGetPremiumClick = () => {
    // If terms checkbox is required and not checked, scroll to it and highlight
    if (requireTermsCheckbox && !isTermsChecked) {
      scrollToById(PAYWALL_TERMS_CHECKBOX_ID, { block: 'center' });
      setTermsHighlighted(true);
      return;
    }

    handleAddToCart();

    if (buttonProps?.onClick) {
      buttonProps.onClick();
    } else {
      show({ stripePlans, billingPortalLink });
    }
  };

  if (isLoadingPlans) {
    return <SelectPlanStyles id={sectionId}>Loading...</SelectPlanStyles>;
  }

  const weeklySubscription = webSubscriptionsModule.getPlanConfig({
    id: 'pro.1w',
    currency: 'USD',
    label: 'Weekly',
    price: 14.99,
    original_price: 0,
  });

  return (
    <SelectPlanStyles id={sectionId}>
      {title && <>{title}</>}
      {isPromoCodeVisible && <PromoCode text={user?.name} />}
      <SelectPlanListStyles ref={plansListRef}>
        {shouldRenderDisabledWeekly && (
          <SubscriptionButton
            selected={false}
            renderDiscount={false}
            subscription={{
              label: weeklySubscription.display,
              id: 'pro.1w',
              price: weeklySubscription.basePriceWithoutSale,
              original_price: weeklySubscription.basePrice,
              currency: 'USD',
            }}
            fullDisabled
          />
        )}
        {stripePlans.map((subscription) => (
          <SubscriptionButton
            selected={checkIsPlanActive(subscription.id)}
            subscription={subscription}
            key={subscription.id}
          />
        ))}
      </SelectPlanListStyles>
      <SubscriptionButtonStyles type="button" onClick={handleGetPremiumClick}>
        {buttonProps?.text || 'GET PREMIUM'}
      </SubscriptionButtonStyles>

      <PaymentDescription
        selectedPlan={selectedPlan}
        monthlyPlan={monthlyPlan}
      />
    </SelectPlanStyles>
  );
};
